// Reverse internal-linking sweep — surfaces top service × location combos from
// each /compare/$topic decision-stage page back into the programmatic combo graph.
// Phase: P-LINK-COMPARISONS-REVERSE
import { Link } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import type { ServiceId } from "@/data/services";
import { SERVICE_HEAD } from "@/data/services-head.gen";
import { getLocation } from "@/data/locations";
import { SERVICE_TO_CATEGORY } from "@/components/RelatedComparisons";

type Category = "translate" | "notary" | "apostille" | "visa";
type Locale = "th" | "en";

// Top 4 service IDs per comparison category — chosen by search volume / intent fit.
const CATEGORY_TOP_SERVICES: Record<Category, ServiceId[]> = {
  translate: [
    "translation",
    "naati",
    "sworn-translation",
    "government-document-translation",
  ],
  notary: ["notary-public", "notary-public-foreigners"],
  apostille: [
    "mfa-legalization",
    "apostille",
    "embassy-legalization",
    "police-clearance",
  ],
  visa: [
    "visa-foreigners-thailand",
    "visa-thai-citizens",
    "marriage-thai-foreign",
    "ltr-elite-visa",
  ],
};

// Reverse lookup from comparison slug → category.
// Mirrors CATEGORY_SLUGS inside RelatedComparisons.
const COMPARISON_TO_CATEGORY: Record<string, Category> = {
  // translate
  "nyc-vs-freelance-translator": "translate",
  "nyc-vs-google-translate": "translate",
  "nyc-vs-translation-app": "translate",
  "nyc-vs-chatgpt": "translate",
  "nyc-vs-diy-translation": "translate",
  "nyc-vs-fiverr-translation": "translate",
  "nyc-vs-court-translator": "translate",
  "nyc-vs-naati-overseas": "translate",
  "nyc-vs-thai-embassy-translation": "translate",
  "nyc-vs-online-translator": "translate",
  "nyc-vs-shipping-document-overseas": "translate",
  // notary
  "nyc-vs-other-law-firms": "notary",
  "nyc-vs-online-notary": "notary",
  "nyc-vs-thai-lawyer-firm-bangkok": "notary",
  "nyc-vs-blockchain-notary": "notary",
  // apostille
  "nyc-vs-mfa-direct": "apostille",
  "nyc-vs-embassy-direct": "apostille",
  "nyc-vs-document-courier": "apostille",
  // visa
  "nyc-vs-immigration-agency": "visa",
  "nyc-vs-immigration-lawyer-overseas": "visa",
  "nyc-vs-vfs-global": "visa",
  "nyc-vs-bts-visa-agent": "visa",
  "nyc-vs-thai-language-school-ed-visa": "visa",
  "nyc-vs-marriage-registration-amphoe": "visa",
};

export function categoryForComparison(slug: string): Category | null {
  if (COMPARISON_TO_CATEGORY[slug]) return COMPARISON_TO_CATEGORY[slug];
  // Fallback: scan SERVICE_TO_CATEGORY by keyword in slug.
  if (slug.includes("translat") || slug.includes("naati") || slug.includes("chatgpt")) return "translate";
  if (slug.includes("notary") || slug.includes("law-firm")) return "notary";
  if (slug.includes("mfa") || slug.includes("embassy") || slug.includes("apostille") || slug.includes("courier")) return "apostille";
  if (slug.includes("visa") || slug.includes("immigration") || slug.includes("marriage")) return "visa";
  void SERVICE_TO_CATEGORY; // keep import alive for tree-shaking parity
  return null;
}

// Curated top-traffic locations — branches + high-intent Bangkok hubs +
// expat hubs nationwide. Kept short so each combo card has real signal.
const TOP_LOCATIONS = [
  "bangkok-hq",
  "hub-asoke-corridor",
  "hub-silom",
  "hub-chiangmai-nimman",
  "hub-phuket-patong",
  "hub-pattaya",
] as const;

const COPY = {
  th: {
    eyebrow: "Near you · บริการในพื้นที่",
    heading: "บริการในเมืองหลักของไทย",
    sub: "พร้อมให้บริการในกรุงเทพฯ และเมืองหลักทั่วประเทศ · รับ-ส่งเอกสารถึงที่ · 4 สำนักงานสาขา",
    cta: "ดูทุกสาขา",
    inLabel: "ใน",
  },
  en: {
    eyebrow: "Near you · Available locations",
    heading: "Service available across Thailand",
    sub: "On-the-ground in Bangkok and every major city · Doorstep pickup · 4 owned branches.",
    cta: "All locations",
    inLabel: "in",
  },
} as const;

export function RelatedServiceLocations({
  comparisonSlug,
  locale,
}: {
  comparisonSlug: string;
  locale: Locale;
}) {
  const category = categoryForComparison(comparisonSlug);
  if (!category) return null;

  const services = CATEGORY_TOP_SERVICES[category]
    .map((id) => SERVICE_HEAD[id])
    .filter(Boolean);
  if (services.length === 0) return null;

  const locs = TOP_LOCATIONS
    .map((s) => getLocation(s))
    .filter((l): l is NonNullable<ReturnType<typeof getLocation>> => Boolean(l));

  // Build up to 8 service × location combo cards — round-robin to vary
  // both axes without front-loading a single service.
  const combos: { service: (typeof services)[number]; loc: (typeof locs)[number] }[] = [];
  for (let i = 0; i < 8; i++) {
    const service = services[i % services.length];
    const loc = locs[i % locs.length];
    if (!service || !loc) continue;
    combos.push({ service, loc });
  }

  const t = COPY[locale];
  const locationsHubPath = locale === "en" ? "/en/locations" : "/locations";
  const comboPath = locale === "en" ? "/en/services/$service/$location" : "/services/$service/$location";

  return (
    <section className="border-t border-slate-200 bg-gradient-to-b from-white to-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-50 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-emerald-700">
              <MapPin className="h-3.5 w-3.5" /> {t.eyebrow}
            </div>
            <h2 className="mt-3 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              {t.heading}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">{t.sub}</p>
          </div>
          <Link
            to={locationsHubPath}
            className="inline-flex items-center gap-1.5 rounded-full bg-navy-deep px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-navy-deep/90"
          >
            {t.cta} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {combos.map(({ service, loc }, i) => {
            const sName = locale === "en" ? service.nameEn : service.nameTh;
            const lName = locale === "en" ? loc.nameEn : loc.nameTh;
            return (
              <Link
                key={`${service.id}-${loc.slug}-${i}`}
                to={comboPath}
                params={{ service: service.slug, location: loc.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-emerald-400/50 hover:shadow-md"
              >
                <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                  <MapPin className="h-3 w-3" /> {lName}
                </div>
                <h3 className="mt-1 line-clamp-2 font-display text-sm font-extrabold text-navy-deep group-hover:text-emerald-700">
                  {sName} {t.inLabel} {lName}
                </h3>
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                  {locale === "en" ? "View service" : "ดูบริการ"} <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
